/** Open career roles for EncycGames careers page (from Career Positions doc). */
export type CareerRoleId =
  | 'business_development_manager'
  | 'marketing_manager'
  | 'sponsorship_manager'
  | 'event_organizer_manager';

export interface CareerRole {
  id: CareerRoleId;
  title: string;
  team: string;
  location: string;
  type: string;
  summary: string;
  responsibilities: string[];
  requirements: string[];
}

export const CAREER_ROLES: CareerRole[] = [
  {
    id: 'business_development_manager',
    title: 'Business Development Manager — Sports Tech (MultiSports | Global)',
    team: 'Commercial',
    location: 'Flexible / Remote with frequent travel',
    type: 'Full-time',
    summary:
      'Lead commercial growth for a multisports learning, training and tournament platform by building partnerships with sports academies, schools, federations, brands, players and coaches worldwide. Own the full sales cycle from lead generation to contract negotiation and post-sale relationship management.',
    responsibilities: [
      'Identify and prioritize target segments (academies, schools, federations, clubs, brands, coaching networks) across regions',
      'Build and maintain a global pipeline through outreach, referrals, events, and channel partnerships',
      'Lead end-to-end sales: discovery, demos, pilots, proposals, negotiation, and closure',
      'Structure commercial models: subscriptions, per-athlete licensing, tournament revenue share, sponsorship, and enterprise deals',
      'Design pilots with clear KPIs and coordinate product, customer success, coaching, and analytics',
      'Use platform analytics to build ROI narratives and feed market feedback into the product roadmap',
    ],
    requirements: [
      '5+ years in B2B sales or partnerships (sports tech, edtech, SaaS, or sports business preferred)',
      'Proven track record closing enterprise or multi-site deals',
      'Strong understanding of sports business models (academies, federations, sponsorships, pathways)',
      'Comfortable with data/analytics and executive presentations',
      'Willingness to travel internationally and work across time zones; fluent English',
    ],
  },
  {
    id: 'marketing_manager',
    title: 'Marketing Manager — Sports Tech (Learning, Training & Tournaments | Global)',
    team: 'Growth / Marketing',
    location: 'Flexible / Remote with international travel',
    type: 'Full-time',
    summary:
      'Lead demand generation, brand positioning, and partner marketing for a multisports platform. Build awareness and pipeline among academies, schools, federations, brands, players and coaches globally through sports domain knowledge, data-driven digital marketing, and field activation.',
    responsibilities: [
      'Define value proposition and messaging for academies, schools, federations, coaches, players and brands',
      'Plan multichannel acquisition campaigns (digital, content, email, SEO, webinars, events)',
      'Create content and communities: case studies, curricula, video tutorials, tournament toolkits',
      'Design co-marketing and on-ground activations with BDMs, federations, academies and brands',
      'Build sales enablement collateral and run product launches / pilot rollouts',
      'Track marketing KPIs (CAC, LTV, MQL→SQL, trial-to-paid) and maintain CRM / automation hygiene',
    ],
    requirements: [
      '5+ years in B2B or B2B2C marketing (sports tech, edtech, SaaS, or consumer sports preferred)',
      'Proven digital acquisition, content, and partner / co-marketing experience',
      'Strong understanding of sports ecosystems and tournament operations',
      'Data-literate with analytics, attribution, and dashboards',
      'Willingness to travel internationally; fluent English',
    ],
  },
  {
    id: 'sponsorship_manager',
    title: 'Sponsorship Manager — Sports & Fitness',
    team: 'Commercial',
    location: 'Flexible / Global',
    type: 'Full-time',
    summary:
      'Drive sponsorship revenue and brand partnerships for a multisports learning, training and tournament platform. Build relationships with academies, federations, fitness brands, corporates, and athlete ambassadors to secure funding, visibility, and long-term collaborations.',
    responsibilities: [
      'Identify and pitch sponsorship opportunities aligned with sports, fitness, and youth development',
      'Develop sponsorship packages (title, event, digital activation, athlete endorsements)',
      'Negotiate contracts and manage sponsor deliverables for ROI and brand visibility',
      'Collaborate with marketing and events on activations across tournaments and digital platforms',
      'Maintain sponsor relationships, reporting, and renewals',
      'Explore cross-sector partnerships (healthcare, wellness, edtech, consumer brands)',
    ],
    requirements: [
      '5+ years in sponsorship sales, sports marketing, or brand partnerships',
      'Strong network in sports, fitness, and consumer brands',
      'Proven track record closing high-value sponsorship deals',
      'Understanding of sports business models, endorsements, and digital activations',
      'Excellent negotiation and presentation skills; willingness to travel',
    ],
  },
  {
    id: 'event_organizer_manager',
    title: 'Event Organizer & Manager — Sports',
    team: 'Operations',
    location: 'Flexible / Global travel required',
    type: 'Full-time',
    summary:
      'Plan, execute, and manage multisport tournaments, training camps, and academy events for a global sports tech platform. Ensure seamless operations, athlete experience, and sponsor/partner satisfaction.',
    responsibilities: [
      'Design end-to-end event plans: venue, scheduling, logistics, staffing, and compliance',
      'Coordinate with federations, academies, schools, and coaches on formats and modules',
      'Manage budgets, vendors, and on-ground operations',
      'Integrate technology (live scoring, analytics, streaming, registration) into events',
      'Ensure athlete safety, safeguarding, and medical protocols',
      'Build scalable frameworks to replicate events across regions and sports',
    ],
    requirements: [
      '5+ years in sports event management, tournament operations, or large-scale event planning',
      'Experience with federations, academies, or sports governing bodies',
      'Strong organizational and project management skills',
      'Knowledge of sports compliance, athlete welfare, and event safety standards',
      'Familiarity with sports tech tools; willingness to travel extensively',
    ],
  },
];

export function getCareerRole(id: string | null | undefined): CareerRole | undefined {
  return CAREER_ROLES.find((r) => r.id === id);
}
